import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Sparkles, ArrowRight, Check, Crown } from 'lucide-react';
import { getServiceBySlug, SERVICES, SERVICE_MATRIX_IDS, type ServiceDef, type PlanTier } from '../../data/services';
import { useAuth } from '../../hooks/useAuth';
import { useComingSoon } from '../../hooks/useComingSoon';
import { useServiceLandingContent } from '../../hooks/useServiceLandingContent';
import { useServiceTestimonials, type Testimonial } from '../../hooks/useServiceTestimonials';
import { useServiceCatalogOverride } from '../../hooks/useServiceCatalogOverrides';
import { useI18n } from '../../hooks/useI18n';
import { usePricing } from '../../contexts/PricingContext';
import { loadPlanAccessMatrix, type AccessMatrix } from '../../lib/planAccessMatrix';
import { useAvulsoProduct } from '../../hooks/useAvulsoProducts';
import { AvulsoCompareModal } from '../../components/shared/AvulsoCompareModal';
import { SynastryWidget, SynastryContrast } from './SynastryWidget';
import { ForecastWidget, ForecastContrast } from './ForecastWidget';

// ─────────────────────────────────────────
// Tier helpers
// ─────────────────────────────────────────
const TIER_ORDER: Record<PlanTier, number> = { free: 0, monthly: 1, annual: 2 };

function tierMeets(currentTier: PlanTier | null | undefined, required: PlanTier): boolean {
  if (!currentTier) return false;
  return (TIER_ORDER[currentTier] ?? 0) >= TIER_ORDER[required];
}

// Derive a plan-note string from the access matrix for a given service.
function computeMatrixPlanNote(service: ServiceDef, matrix: AccessMatrix): string | null {
  const ids = SERVICE_MATRIX_IDS[service.slug];
  if (!ids) return null;
  const isEnabled = (tier: PlanTier) =>
    ids.some(id => (matrix[id] as Record<string, { enabled?: boolean }> | undefined)?.[tier]?.enabled);
  const free    = isEnabled('free');
  const monthly = isEnabled('monthly');
  const annual  = isEnabled('annual');
  if (free)              return 'Disponível em todos os planos';
  if (monthly && annual) return 'Disponível nos Planos Mensal e Anual';
  if (monthly)           return 'Disponível no Plano Mensal';
  if (annual)            return 'Exclusivo do Plano Anual';
  return null;
}

// Build a list of service names enabled for a tier (optionally excluding those in prevTier).
function buildTierBullets(matrix: AccessMatrix | null, tier: PlanTier, prevTier?: PlanTier): string[] {
  if (!matrix) return [];
  const enabled = (t: PlanTier) => new Set(
    Object.entries(matrix)
      .filter(([id, row]) => !id.startsWith('_') && (row as Record<string, { enabled?: boolean }>)[t]?.enabled)
      .map(([id]) => id)
  );
  const enabledIds = enabled(tier);
  const prevIds    = prevTier ? enabled(prevTier) : new Set<string>();
  const seen = new Set<string>();
  const names: string[] = [];
  for (const [slug, matrixIds] of Object.entries(SERVICE_MATRIX_IDS)) {
    const hasAny = matrixIds.some(id => enabledIds.has(id));
    const isNew  = prevTier ? matrixIds.some(id => enabledIds.has(id) && !prevIds.has(id)) : hasAny;
    if (isNew && !seen.has(slug)) {
      seen.add(slug);
      const svc = SERVICES.find(s => s.slug === slug);
      if (svc) names.push(svc.name);
    }
  }
  return names;
}

// TIER_LABELS is resolved at render time via useI18n(), see useTierLabels()
function useTierLabels(): Record<PlanTier, string> {
  const { t } = useI18n();
  return {
    free:    t('pages.servicos.tierFree'),
    monthly: t('pages.servicos.tierMonthly'),
    annual:  t('pages.servicos.tierAnnual'),
  };
}

// ─────────────────────────────────────────
// ServiceLandingPage, /servicos/:slug
// Public marketing page for each service. Works for logged-out visitors
// and logged-in users without access (upgrade CTA variants come in a
// later chunk).
// ─────────────────────────────────────────

export function ServiceLandingPage({ slug: slugProp }: { slug?: string } = {}) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug;
  const { lang } = useI18n();
  const baseService  = slug ? getServiceBySlug(slug) : undefined;
  const comingSoonState = useComingSoon(baseService?.slug);
  const comingSoon = comingSoonState === true;
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);

  useEffect(() => { loadPlanAccessMatrix().then(setMatrix); }, []);

  // AI-generated override for benefits/forWho/faqs/previewText. Hook is
  // always called to respect the rules of hooks; when baseService is
  // missing we pass a stub and the result is discarded below.
  const stub: ServiceDef = baseService ?? {
    slug: '', appRoute: '', name: '', subtitle: '', description: '', icon: '',
    requiredTier: 'free', benefits: [], forWho: [], faqs: [], previewText: '',
  };
  const dynamic = useServiceLandingContent(stub, lang);
  const catalogOverride = useServiceCatalogOverride(baseService?.slug);

  if (!baseService) {
    return <Navigate to="/" replace />;
  }

  // Merge: admin override (short fields) + static catalog + AI-generated
  // override for the long-form fields.
  const matrixPlanNote = matrix ? computeMatrixPlanNote(baseService, matrix) : null;
  const service: ServiceDef = {
    ...baseService,
    name:        catalogOverride?.name        ?? baseService.name,
    subtitle:    catalogOverride?.subtitle    ?? baseService.subtitle,
    description: catalogOverride?.description ?? baseService.description,
    planNote:    catalogOverride?.planNote    ?? matrixPlanNote ?? baseService.planNote,
    benefits:    dynamic.benefits.length    ? dynamic.benefits    : baseService.benefits,
    forWho:      dynamic.forWho.length      ? dynamic.forWho      : baseService.forWho,
    faqs:        dynamic.faqs.length        ? dynamic.faqs        : baseService.faqs,
    previewText: dynamic.previewText        ? dynamic.previewText : baseService.previewText,
  };

  return (
    <>
      <Helmet>
        <title>{`${service.name}, Professya`}</title>
        <meta name="description" content={service.description.slice(0, 160)} />
      </Helmet>

      <div className="cosmic-page">
        <Hero service={service} comingSoon={comingSoon} />
        {!comingSoon && service.special === 'synastry' && (
          <>
            <SynastryWidget />
            <SynastryContrast />
          </>
        )}
        {!comingSoon && service.special === 'forecast' && (
          <>
            <ForecastWidget />
            <ForecastContrast />
          </>
        )}
        <Benefits service={service} />
        <ForWho service={service} />
        <Preview service={service} />
        {!comingSoon && <Plans service={service} matrix={matrix} />}
        <Testimonials slug={service.slug} />
        <Faq service={service} />
        <FinalCta service={service} comingSoon={comingSoon} />
      </div>
    </>
  );
}

// ─── 1. Hero ──────────────────────────────────────────────────────
function Hero({ service, comingSoon }: { service: ServiceDef; comingSoon: boolean }) {
  const { profile } = useAuth();
  const { t } = useI18n();
  const ctaHref = profile ? service.appRoute : '/register';
  const ctaLabel = profile ? `${t('pages.servicos.heroCtaAccess')} ${service.name}` : t('pages.servicos.heroCtaStart');

  const planBadge =
    service.requiredTier === 'free'    ? t('pages.servicos.heroBadgeFree') :
    service.requiredTier === 'monthly' ? t('pages.servicos.heroBadgeMonthly') :
                                         t('pages.servicos.heroBadgeAnnual');

  return (
    <section style={{
      padding: '6rem 1.5rem 4rem',
      textAlign: 'center',
      maxWidth: '820px',
      margin: '0 auto',
    }}>
      <div style={{
        fontSize: 'clamp(3rem, 10vw, 5rem)',
        lineHeight: 1,
        marginBottom: '1rem',
      }}>
        {service.icon}
      </div>
      <h1 className="font-display" style={{
        fontSize: 'clamp(2rem, 6vw, 3rem)',
        color: 'var(--text-primary)',
        marginBottom: '0.5rem',
        lineHeight: 1.15,
      }}>
        {service.name}
      </h1>
      <p style={{
        fontSize: 'clamp(1rem, 3vw, 1.25rem)',
        color: 'var(--petrol)',
        marginBottom: '1.5rem',
        fontWeight: 500,
      }}>
        {service.subtitle}
      </p>
      <p style={{
        fontSize: '1rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.7,
        maxWidth: '640px',
        margin: '0 auto 2rem',
      }}>
        {service.description}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
        {comingSoon ? (
          <button
            disabled
            className="btn btn-primary btn-lg"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', opacity: 0.5, cursor: 'not-allowed' }}
          >
            {t('pages.servicos.comingSoonBtn')}
          </button>
        ) : (
          <Link to={ctaHref} className="btn btn-primary btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            {ctaLabel} <ArrowRight size={18} />
          </Link>
        )}
        <span style={{
          fontSize: 'var(--text-caption)',
          color: comingSoon ? 'var(--petrol)' : 'var(--text-muted)',
          padding: '0.3rem 0.75rem',
          borderRadius: '999px',
          border: comingSoon ? '1px solid var(--petrol)' : '1px solid var(--border-subtle)',
          background: comingSoon ? 'rgba(245,200,66,0.12)' : 'rgba(245,200,66,0.05)',
          fontWeight: comingSoon ? 700 : 400,
          letterSpacing: comingSoon ? '0.06em' : 'normal',
          textTransform: comingSoon ? 'uppercase' : 'none',
        }}>
          {comingSoon ? `✦ ${t('pages.servicos.comingSoonBadge')}` : `✦ ${service.planNote ?? planBadge}`}
        </span>
      </div>
    </section>
  );
}

// ─── 2. Benefits ──────────────────────────────────────────────────
function Benefits({ service }: { service: ServiceDef }) {
  const { t } = useI18n();
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '2.5rem',
      }}>
        {t('pages.servicos.benefitsTitle')}
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem',
      }}>
        {service.benefits.map((b, i) => (
          <div key={i} className="card" style={{
            padding: '1.5rem',
            border: '1px solid var(--border-subtle)',
            background: 'rgba(255,255,255,0.03)',
          }}>
            <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{b.icon}</div>
            <h3 style={{
              fontSize: '1.05rem',
              color: 'var(--text-primary)',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}>
              {b.title}
            </h3>
            <p style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
              {b.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── 3. For Who ───────────────────────────────────────────────────
function ForWho({ service }: { service: ServiceDef }) {
  const { t } = useI18n();
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '820px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '2rem',
      }}>
        {t('pages.servicos.forWhoTitle')}
      </h2>
      <ul style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}>
        {service.forWho.map((item, i) => (
          <li key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}>
            <Sparkles size={18} style={{ color: 'var(--petrol)', flexShrink: 0, marginTop: '0.15rem' }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── 4. Preview (blurred) ─────────────────────────────────────────
function Preview({ service }: { service: ServiceDef }) {
  const { t } = useI18n();
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '820px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '1rem',
      }}>
        {t('pages.servicos.previewTitle')}
      </h2>
      <p style={{
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: 'var(--text-caption)',
        marginBottom: '1.5rem',
      }}>
        {t('pages.servicos.previewSubtitle')}
      </p>

      <div style={{ position: 'relative' }}>
        <div style={{
          padding: '2rem',
          background: 'linear-gradient(180deg, rgba(245,200,66,0.05), rgba(0,0,0,0))',
          border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-card)',
          color: 'var(--text-secondary)',
          lineHeight: 1.75,
          fontSize: '1rem',
          fontStyle: 'italic',
          filter: 'blur(1.5px)',
          userSelect: 'none',
          pointerEvents: 'none',
        }}>
          {service.previewText}
        </div>
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, rgba(10,22,40,0) 40%, rgba(10,22,40,0.8) 100%)',
          borderRadius: 'var(--radius-card)',
          pointerEvents: 'none',
        }}>
          <span style={{
            background: 'rgba(10,22,40,0.75)',
            color: 'var(--petrol)',
            padding: '0.5rem 1rem',
            borderRadius: '999px',
            border: '1px solid var(--border-gold)',
            fontSize: 'var(--text-caption)',
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}>
            {t('pages.servicos.previewUnlock')}
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── 5. Plans (dynamic based on auth state) ──────────────────────
function Plans({ service, matrix }: { service: ServiceDef; matrix: AccessMatrix | null }) {
  const { profile } = useAuth();
  const hasAccess = tierMeets(profile?.subscription_tier as PlanTier | undefined, service.requiredTier);

  // Variant C, already has access
  if (profile && hasAccess) {
    return <AlreadyHaveAccess service={service} />;
  }

  // Variant B, logged in, needs upgrade
  if (profile) {
    return (
      <UpgradeCta
        service={service}
        currentTier={(profile.subscription_tier as PlanTier) ?? 'free'}
      />
    );
  }

  // Variant A, not logged in, show all plans
  return <AllPlans service={service} matrix={matrix} />;
}

// ─── 5A. Already has access ──────────────────────────────────────
function AlreadyHaveAccess({ service }: { service: ServiceDef }) {
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '720px', margin: '0 auto' }}>
      <div className="card" style={{
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid var(--border-gold)',
        background: 'linear-gradient(180deg, rgba(74,222,128,0.08), rgba(0,0,0,0))',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✦</div>
        <h2 className="font-display" style={{
          fontSize: '1.5rem',
          color: 'var(--text-primary)',
          marginBottom: '0.75rem',
        }}>
          Você já tem acesso a este serviço
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          Seu plano atual inclui {service.name}. Clique abaixo para acessar agora.
        </p>
        <Link to={service.appRoute} className="btn btn-primary btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          Acessar {service.name} <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
}

// ─── 5B. Upgrade CTA (logged-in user without access) ─────────────
function UpgradeCta({ service, currentTier }: { service: ServiceDef; currentTier: PlanTier }) {
  const { getProduct } = usePricing();
  const TIER_LABELS = useTierLabels();
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [showAvulsoModal, setShowAvulsoModal] = useState(false);
  const avulsoProduct = useAvulsoProduct(service.slug);

  useEffect(() => { loadPlanAccessMatrix().then(setMatrix); }, []);

  const targetTier  = service.requiredTier;
  const monthlyProd = getProduct('monthly_sub');
  const annualProd  = getProduct('annual_sub');

  // Savings badge: how much cheaper annual is per-month vs monthly
  const monthlyCents = monthlyProd?.price_brl ?? 0;
  const annualMonthlyCents = annualProd ? annualProd.price_brl / 12 : 0;
  const savingsPct = monthlyCents > 0
    ? Math.round((1 - annualMonthlyCents / monthlyCents) * 100)
    : 0;

  // Monthly bullets: what monthly includes (specific items)
  function monthlyFeatures(): { label: string; intro?: boolean }[] {
    const base = [
      { label: `Acesso completo a ${service.name}` },
      { label: 'Mapa Natal completo com interpretação' },
      { label: 'Previsão personalizada (diária, semanal e mensal)' },
      { label: 'Sinastria, 3 análises/mês' },
      { label: 'Tarot Personalizado ilimitado (3 e 10 cartas)' },
    ];
    if (!matrix) return base;
    // Add count of total enabled services from matrix
    const count = Object.entries(matrix).filter(([id, tiers]) =>
      !id.startsWith('_') && (tiers as Record<string, { enabled?: boolean }>)['monthly']?.enabled
    ).length;
    return [...base, { label: `${count} serviços incluídos no plano` }];
  }

  // Annual bullets: "everything in monthly, plus these extras"
  function annualExtras(): { label: string; intro?: boolean }[] {
    return [
      { label: 'Tudo do Plano Mensal, mais:', intro: true },
      { label: 'Previsão Anual Personalizada' },
      { label: 'Sinastria ilimitada (sem limite mensal)' },
      { label: 'Astro Coach, programa de 12 semanas' },
      { label: 'Agenda Astrológica · Acesso antecipado' },
      { label: 'Suporte prioritário' },
    ];
  }

  const showBothPlans = targetTier === 'monthly'; // free → show monthly + annual upsell

  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: showBothPlans ? '940px' : '820px', margin: '0 auto' }}>
      <h2 className="font-display" style={{ fontSize: 'clamp(1.5rem,4vw,2rem)', color: 'var(--text-primary)', textAlign: 'center', marginBottom: '0.75rem' }}>
        Você está no Plano {TIER_LABELS[currentTier]}
      </h2>
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '1.05rem', marginBottom: '2rem', lineHeight: 1.6 }}>
        {service.name} está disponível a partir do Plano {TIER_LABELS[targetTier]}.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: showBothPlans ? 'repeat(auto-fit,minmax(220px,1fr))' : 'repeat(auto-fit,minmax(240px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>

        {/* Current plan card (grayed out) */}
        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', opacity: 0.65 }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.25rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Seu plano atual</p>
          <h3 style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem', fontWeight: 600 }}>{TIER_LABELS[currentTier]}</h3>
          <p style={{ fontSize: 'var(--text-body)', color: 'var(--text-tertiary)' }}>Sem acesso a {service.name}</p>
        </div>

        {/* Monthly plan card */}
        <div className="card" style={{ padding: '1.5rem', border: `2px solid ${showBothPlans ? 'var(--border-gold)' : 'var(--petrol)'}`, background: 'linear-gradient(180deg,rgba(245,200,66,0.06),rgba(0,0,0,0))', position: 'relative' }}>
          {!showBothPlans && (
            <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--petrol)', color: '#000', fontSize: '0.7rem', padding: '2px 10px', borderRadius: '999px', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
              ✦ RECOMENDADO
            </div>
          )}
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', marginBottom: '0.25rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Faça upgrade</p>
          <h3 style={{ fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: '0.4rem', fontWeight: 600 }}>Plano {TIER_LABELS['monthly']}</h3>
          {monthlyProd && (
            <p style={{ color: 'var(--petrol)', fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              R${monthlyProd.price_brl.toFixed(2).replace('.', ',')}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-tertiary)' }}>/mês</span>
            </p>
          )}
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {monthlyFeatures().map(({ label }) => <PlanBullet key={label}>{label}</PlanBullet>)}
          </ul>
          <Link to="/pricing" className="btn btn-primary" style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
            <Crown size={16} /> Assinar Mensal
          </Link>
        </div>

        {/* Annual plan card, always shown alongside monthly */}
        {showBothPlans && (
          <div className="card" style={{ padding: '1.5rem', border: '2px solid var(--petrol)', background: 'linear-gradient(180deg,rgba(245,200,66,0.1),rgba(0,0,0,0))', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--petrol)', color: '#000', fontSize: '0.7rem', padding: '2px 10px', borderRadius: '999px', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
              ✦ MELHOR CUSTO-BENEFÍCIO
            </div>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', marginBottom: '0.25rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Mais popular</p>
            <h3 style={{ fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: '0.4rem', fontWeight: 600 }}>Plano {TIER_LABELS['annual']}</h3>
            {annualProd && (
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ color: 'var(--petrol)', fontSize: '1.3rem', fontWeight: 700 }}>
                  R${(annualProd.price_brl / 12).toFixed(2).replace('.', ',')}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-tertiary)' }}>/mês</span>
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  R${annualProd.price_brl.toFixed(2).replace('.', ',')} cobrado anualmente
                  {savingsPct > 0 && <span style={{ marginLeft: '0.5rem', background: 'rgba(74,222,128,0.15)', color: '#4ade80', borderRadius: '4px', padding: '1px 6px', fontWeight: 700 }}>Economize {savingsPct}%</span>}
                </p>
              </div>
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {annualExtras().map(({ label, intro }) =>
                intro
                  ? <li key={label} style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic', margin: '0 0 0.2rem' }}>{label}</li>
                  : <PlanBullet key={label}>{label}</PlanBullet>
              )}
            </ul>
            <Link to="/pricing" className="btn btn-primary" style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', background: 'var(--petrol)', color: '#000' }}>
              <Crown size={16} /> Assinar Anual
            </Link>
          </div>
        )}
      </div>

      <p style={{ textAlign: 'center' }}>
        <Link to="/pricing" style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textDecoration: 'underline' }}>
          Ver todos os planos
        </Link>
      </p>

      {/* Avulso purchase option */}
      {avulsoProduct && (
        <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Ou prefere não assinar agora?
          </p>
          <button
            onClick={() => setShowAvulsoModal(true)}
            className="btn btn-ghost"
            style={{ fontSize: 'var(--text-caption)', borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}
          >
            Comprar {service.name} avulso por R${avulsoProduct.price_brl.toFixed(2).replace('.', ',')}
          </button>
        </div>
      )}

      {showAvulsoModal && avulsoProduct && (
        <AvulsoCompareModal
          serviceName={service.name}
          serviceSlug={service.slug}
          avulsoPrice={avulsoProduct.price_brl}
          avulsoDesc={avulsoProduct.description}
          monthlyProd={monthlyProd}
          annualProd={annualProd}
          onClose={() => setShowAvulsoModal(false)}
        />
      )}
    </section>
  );
}

// ─── 5C. All plans (not logged in) ───────────────────────────────
function AllPlans({ service, matrix }: { service: ServiceDef; matrix: AccessMatrix | null }) {
  const { getProduct } = usePricing();
  const { t } = useI18n();
  const TIER_LABELS = useTierLabels();
  const monthly = getProduct('monthly_sub');
  const annual  = getProduct('annual_sub');

  const monthlyPrice = monthly ? `R$${monthly.price_brl.toFixed(2).replace('.', ',')}` : '-';
  const annualPrice  = annual  ? `R$${annual.price_brl.toFixed(2).replace('.', ',')}`  : '-';

  // Recommended plan = minimum tier that grants access
  const recommendedTier: PlanTier = service.requiredTier;

  // Dynamic bullets from admin matrix; fallback to static if matrix not loaded
  const freeDynamic    = buildTierBullets(matrix, 'free');
  const monthlyDynamic = buildTierBullets(matrix, 'monthly', 'free');
  const annualDynamic  = buildTierBullets(matrix, 'annual', 'monthly');

  const plans: Array<{ tier: PlanTier; title: string; price: string; per: string; bullets: string[]; cta: string; href: string }> = [
    {
      tier:  'free',
      title: 'Gratuito',
      price: 'R$0',
      per:   'sempre',
      bullets: freeDynamic.length ? freeDynamic : [
        'Mapa Astral completo',
        'I Ching e Numerologia básica',
        'Fases da lua e calendário lunar',
      ],
      cta:  'Criar conta grátis',
      href: '/register',
    },
    {
      tier:  'monthly',
      title: 'Mensal',
      price: monthlyPrice,
      per:   '/mês',
      bullets: monthlyDynamic.length
        ? ['Tudo do Gratuito, mais:', ...monthlyDynamic, 'Cancele a qualquer momento']
        : ['Tudo do gratuito', 'Previsões completas · Sinastria · AstroMatch', 'Tarot completo · Astrocartografia · Agenda', 'Cancele a qualquer momento'],
      cta:  'Assinar mensal',
      href: '/pricing',
    },
    {
      tier:  'annual',
      title: 'Anual',
      price: annualPrice,
      per:   '/ano',
      bullets: annualDynamic.length
        ? ['Tudo do Mensal, mais:', ...annualDynamic, 'Suporte prioritário']
        : ['Tudo do mensal, com cotas ampliadas', 'Astro Coach, programa de 12 semanas exclusivo', 'Economize vs o plano mensal', 'Suporte prioritário'],
      cta:  'Assinar anual',
      href: '/pricing',
    },
  ];

  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '0.75rem',
      }}>
        {t('pages.servicos.plansTitle')}
      </h2>
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '2.5rem' }}>
        {service.name} está disponível a partir do Plano {TIER_LABELS[recommendedTier]}.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '1rem',
      }}>
        {plans.map(p => {
          const isRecommended = p.tier === recommendedTier;
          return (
            <div key={p.tier} className="card" style={{
              padding: '1.75rem 1.5rem',
              border: isRecommended ? '2px solid var(--petrol)' : '1px solid var(--border-subtle)',
              background: isRecommended ? 'linear-gradient(180deg, rgba(245,200,66,0.08), rgba(0,0,0,0))' : 'rgba(255,255,255,0.03)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}>
              {isRecommended && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--petrol)',
                  color: '#000',
                  fontSize: '0.7rem',
                  padding: '2px 10px',
                  borderRadius: '999px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}>
                  {t('pages.servicos.plansRecommended')}
                </div>
              )}
              <h3 style={{
                fontSize: '1.25rem',
                color: 'var(--text-primary)',
                marginBottom: '0.25rem',
                fontWeight: 600,
              }}>
                {p.title}
              </h3>
              <p style={{
                color: isRecommended ? 'var(--petrol)' : 'var(--text-primary)',
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '1rem',
              }}>
                {p.price}
                <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: '0.25rem' }}>
                  {p.per}
                </span>
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                flex: 1,
              }}>
                {p.bullets.map((b, i) => (
                  <PlanBullet key={i}>{b}</PlanBullet>
                ))}
              </ul>
              <Link
                to={p.href}
                className={isRecommended ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ width: '100%', textAlign: 'center' }}
              >
                {p.cta}
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Plan bullet helper ──────────────────────────────────────────
function PlanBullet({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', lineHeight: 1.5 }}>
      <Check size={14} style={{ color: 'var(--petrol)', flexShrink: 0, marginTop: '0.25rem' }} />
      <span>{children}</span>
    </li>
  );
}

// ─── 6. FAQ ───────────────────────────────────────────────────────
function Faq({ service }: { service: ServiceDef }) {
  const { t } = useI18n();
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '820px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '2rem',
      }}>
        {t('pages.servicos.faqTitle')}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {service.faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255,255,255,0.03)',
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setOpen(isOpen ? null : i)}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
                aria-expanded={isOpen}
              >
                <span>{f.q}</span>
                <ChevronDown size={18} style={{
                  color: 'var(--petrol)',
                  transition: 'transform 200ms ease',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  flexShrink: 0,
                }} />
              </button>
              {isOpen && (
                <div style={{
                  padding: '0 1.25rem 1.1rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7,
                  fontSize: 'var(--text-body)',
                }}>
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── 6b. Testimonials (hidden if none configured) ────────────────
function Testimonials({ slug }: { slug: string }) {
  const items = useServiceTestimonials(slug);
  const { t } = useI18n();
  if (!items.length) return null;
  return (
    <section style={{ padding: '4rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '2rem',
      }}>
        {t('pages.servicos.testimonialsTitle')}
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
        gap: '1rem',
      }}>
        {items.map((t, i) => <TestimonialCard key={i} t={t} />)}
      </div>
    </section>
  );
}

function TestimonialCard({ t }: { t: Testimonial }) {
  const initial = (t.name?.trim()?.[0] ?? '✦').toUpperCase();
  return (
    <div className="card" style={{
      padding: '1.5rem',
      border: '1px solid var(--border-subtle)',
      background: 'rgba(255,255,255,0.03)',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    }}>
      <p style={{
        color: 'var(--text-secondary)',
        lineHeight: 1.7,
        fontStyle: 'italic',
        margin: 0,
      }}>
        "{t.text}"
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-gold)',
          color: 'var(--petrol)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}>
          {initial}
        </div>
        <div>
          <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-label)', fontWeight: 600, margin: 0 }}>
            {t.name}
          </p>
          {t.city && (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
              {t.city}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── 7. Final CTA ─────────────────────────────────────────────────
function FinalCta({ service, comingSoon }: { service: ServiceDef; comingSoon: boolean }) {
  const { profile } = useAuth();
  const { t } = useI18n();
  const primaryHref  = profile ? service.appRoute : '/register';
  const primaryLabel = profile
    ? `${t('pages.servicos.heroCtaAccess')} ${service.name}`
    : t('pages.servicos.finalCtaCreateAccount');

  if (comingSoon) {
    return (
      <section style={{ padding: '5rem 1.5rem', textAlign: 'center', maxWidth: '720px', margin: '0 auto' }}>
        <h2 className="font-display" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', color: 'var(--text-primary)', marginBottom: '1rem' }}>
          {service.name} ✦
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '1.05rem', lineHeight: 1.7 }}>
          {t('pages.servicos.comingSoonDesc')}
        </p>
        <span style={{
          display: 'inline-block',
          padding: '0.5rem 1rem',
          borderRadius: '999px',
          border: '1px solid var(--petrol)',
          color: 'var(--petrol)',
          fontSize: 'var(--text-caption)',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          background: 'rgba(245,200,66,0.1)',
        }}>
          {t('pages.servicos.comingSoonBtn')}
        </span>
      </section>
    );
  }

  return (
    <section style={{
      padding: '5rem 1.5rem',
      textAlign: 'center',
      maxWidth: '720px',
      margin: '0 auto',
    }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
        color: 'var(--text-primary)',
        marginBottom: '1rem',
      }}>
        {t('pages.servicos.finalCtaTitle')}
      </h2>
      <p style={{
        color: 'var(--text-secondary)',
        marginBottom: '2rem',
        fontSize: '1.05rem',
        lineHeight: 1.7,
      }}>
        {profile
          ? `${t('pages.servicos.heroCtaAccess')} ${service.name}`
          : t('pages.servicos.finalCtaAnonDesc')}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <Link to={primaryHref} className="btn btn-primary btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          {primaryLabel} <ArrowRight size={18} />
        </Link>
        {!profile && (
          <Link to="/login" className="btn btn-ghost btn-lg">
            {t('pages.servicos.finalCtaHaveAccount')}
          </Link>
        )}
      </div>
    </section>
  );
}
